import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { TipoProductos , TipoProductosPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch , handleAxiosError} from '@/shared';


const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchTipoProductos = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetTipoProductosParams) => {
  return useQuery({
    queryKey: ['tipo-productos', page, nombre, page_size],
    queryFn: async () => {
      return getTipoProductos({ page, nombre, page_size });
    },
  });
};

export const useGetTipoProducto = (id: string) => {
  return useQuery({
    queryKey: ['tipo-productos', id],
    queryFn: async () => {
      return getTipoProducto(id);
    },
  });
};

export const useCreateTipoProductos = (navigate: NavigateFunction, returnUrl: string
  ,
  enableNavigation?: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoProductos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipo-productos'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const useUpdateTipoProductos = (navigate: NavigateFunction, returnUrl: string, noState?: boolean,
  enableNavigation?: boolean) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoProductos,
    onSuccess: () => {
      noState &&  queryClient.invalidateQueries({ queryKey: ['tipo-productos'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const useDeleteTipoProductos = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: async (id: string) => removeTipoProductos(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipo-productos'] });
      setConfirmDialogIsOpen(false);
    },
    onError: (error) => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetTipoProductosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreatetipoProductosParams = Omit<TipoProductos, 'id_tipo_producto'>;
export type UpdatetipoProductosParams = {
  id: string;
  data: CreatetipoProductosParams;
};

export const getTipoProductos = async (params?: GetTipoProductosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<TipoProductosPagingRes>(
    `${urlBackend}api/v1/tipos-producto/?${queryParams}`,
    true
  );
};

export const getTipoProducto = async (id: string) => {
  try{
    return await get<TipoProductos>(`${urlBackend}api/tipos-producto/${id}/`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createTipoProductos = async (data: CreatetipoProductosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoProductos>(`${urlBackend}api/v1/tipos-producto/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoProductos = async ({ id, data }: UpdatetipoProductosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoProductos>(`${urlBackend}api/v1/tipos-producto/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};
export const removeTipoProductos= async (id:string)=>{
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<TipoProductos>(`${urlBackend}api/tipos-producto/${id}/`, true);
  setAlert(true, res.message, res.status);
  return res;
}